import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Button,
  Spin,
  Space,
  Modal,
  message,
  Popconfirm,
  Alert,
} from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, gql, useLazyQuery, useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import {
  EyeOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import moment from 'moment';

import DataGrid from '../../components/DataGrid';
import * as Config from '../../appConfig';
import { activePeriod, disabledDate } from '../../utils/date';

import {
  // updateTableMetaData,
  updateGridMetaData,
  resetGridFilters,
} from '../../store/reducers/expenseSlice';
import { INCENTIVE_IMPORT_LIST_FIELDS } from '../../apollo/fragments';
import {
  IMPORT_INCENTIVE_MUTATION,
  DELETE_INCENTIVE_IMPORT_MUTATION,
} from '../../apollo/mutations';
import {
  EMPLOYEE_LOOKUP_QUERY,
  PORTFOLIO_OPTIONS_QUERY,
} from '../../apollo/queries';

import AddIncentiveImportForm from '../../components/Expense/Forms/AddIncentiveImportForm';
import UploadFile from '../UploadFile';

var fileDownload = require('js-file-download');

const INCENTIVE_IMPORTS_QUERY = gql`
  ${INCENTIVE_IMPORT_LIST_FIELDS}
  query GetIncentiveImports(
    $first: Int
    $page: Int
    $filters: [QueryGetIncentiveImportsFilter]
    $sort: QueryGetIncentiveImportsSorter
  ) {
    incentiveImports(
      first: $first
      page: $page
      filters: $filters
      sort: $sort
    ) {
      paginatorInfo {
        count
        currentPage
        total
      }
      data {
        ...IncentiveImportsListFields
      }
    }
  }
`;

const IncentiveImports = ({ gridId, incentivesGridId }) => {
  const { metaData, grids } = useSelector((state) => state.expense);
  const {
    user: { ability },
  } = useSelector((state) => state.login);
  const dispatch = useDispatch();

  const grid = grids.find((i) => i.id === gridId);
  const gridIncentives = grids.find((i) => i.id === incentivesGridId);

  const history = useHistory();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [
    showAddIncentiveImportModal,
    setShowAddIncentiveImportModal,
  ] = useState(false);
  const [
    showAddIncentiveImportErrorModal,
    setShowAddIncentiveImportErrorModal,
  ] = useState(false);
  const [importErrors, setImportErrors] = useState('');
  const [activeIncentiveImport, setActiveIncentiveImport] = useState(null);
  const [fileToImport, setFileToImport] = useState(null);

  const [freezePrevMonth, currentMonth, prevMonth] = activePeriod();

  const queryVariables = useMemo(() => {
    return {
      first: grid.metaData.pagination.pageSize,
      page: grid.metaData.pagination.page,
      filters: grid.metaData.filters,
      sort: grid.metaData.sorting,
    };
  }, [
    grid.metaData.pagination.pageSize,
    grid.metaData.pagination.page,
    grid.metaData.filters,
    grid.metaData.sorting,
  ]);

  const { loading, data, error, refetch } = useQuery(INCENTIVE_IMPORTS_QUERY, {
    variables: queryVariables,
  });

  console.log('ERROR', error);

  const [
    lookupEmployee,
    { data: lookupData, loading: fetching },
  ] = useLazyQuery(EMPLOYEE_LOOKUP_QUERY);

  const { loading: loadingPortfolios, data: portfolios } = useQuery(
    PORTFOLIO_OPTIONS_QUERY
  );

  const [incentiveImport, { loading: adding }] = useMutation(
    IMPORT_INCENTIVE_MUTATION
  );
  const [deleteIncentiveImport, { loading: deleting }] = useMutation(
    DELETE_INCENTIVE_IMPORT_MUTATION
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  const onSuccessMutation = (mesageText = 'Details updated successfully!') => {
    refetch(queryVariables);
    message.success(mesageText);
  };

  const columns = [
    {
      title: 'Sl.No',
      dataIndex: 'id',
      key: 'id',
      serverKey: 'ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      className: 'width-100',
    },
    {
      title: 'Portfolio',
      dataIndex: ['portfolio', 'name'],
      key: 'portfolio',
      serverKey: 'PORTFOLIO',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'For Month',
      dataIndex: 'for_month',
      key: 'for_month',
      serverKey: 'MONTH',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'date',
      format: 'date',
    },
    {
      title: 'Records Processed',
      dataIndex: 'records_processed',
      key: 'records_processed',
      serverKey: 'NO_OF_RECORDS',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      serverKey: 'NOTE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Imported By',
      dataIndex: ['createdBy', 'name'],
      key: 'created_by',
      serverKey: 'USER',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'string',
    },
    {
      title: 'Imported Date',
      dataIndex: 'created_at',
      key: 'created_at',
      serverKey: 'CREATED_AT',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'date',
      format: 'date',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      serverKey: 'ACTIONS',
      sorter: false,
      canExclude: false,
      visible: true,
      ellipsis: false,
      disableSearch: true,
      className: 'width-100',
      render: (value, record) => {
        const for_month = moment(record.for_month);
        const isEditable = disabledDate(
          for_month,
          freezePrevMonth,
          currentMonth,
          prevMonth
        );
        return (
          <Space>
            {ability.toEditAllowance && (
              <Button
                type="default"
                size="small"
                icon={<DownloadOutlined />}
                title="Download"
                onClick={() => {
                  onMediaDownload(record);
                }}
              ></Button>
            )}
            {ability.toEditAllowance && (
              <Button
                type="default"
                size="small"
                icon={<EyeOutlined />}
                title="View"
                onClick={() => {
                  const updatedMetaData = { ...gridIncentives.metaData };
                  updatedMetaData.filters = [{
                    column: 'IMPORT_ID',
                    value: record.id,
                  }];
                  dispatch(
                    updateGridMetaData({
                      gridId: incentivesGridId,
                      metaData: updatedMetaData,
                    })
                  );
                  history.push('/expense/incentives');
                }}
              ></Button>
            )}
            {ability.toDeleteAllowance && (
              <Popconfirm
                title="This will delete all records associated with this import, Are you sure to delete this?"
                onConfirm={() => {
                  onDeleteConfirm(record);
                }}
                onCancel={() => {}}
                okText="Delete"
                cancelText="No"
                placement="rightTop"
                disabled={isEditable}
              >
                <Button
                  type="ghost"
                  size="small"
                  icon={<DeleteOutlined />}
                  title="Delete"
                  danger
                  disabled={isEditable}
                ></Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  // const columnsForExport = [...columns];

  useEffect(() => {
    if (grid) {
      console.log('refetch for metaData with///', queryVariables);
      refetch(queryVariables);
    }
  }, [dispatch, refetch, grid, queryVariables]);

  const onChange = (updatedMetaData) => {
    console.log('onChange///', updatedMetaData);
    // dispatch(
    //   updateTableMetaData({
    //     metaData: {
    //       ...metaData,
    //       ...updatedMetaData,
    //     },
    //   })
    // );
    dispatch(
      updateGridMetaData({
        gridId,
        metaData: updatedMetaData,
      })
    );
  };

  const onIncentiveImportSubmit = (values, callBack) => {
    console.log('onIncentiveImportSubmit called with///', values);
    const variables = {
      input: {
        file: {
          name: fileToImport.name,
          mime: fileToImport.mime,
          path: fileToImport.path,
        },
        portfolio_id: parseInt(values.portfolio_id, 10),
        for_month: values.for_month
          ? values.for_month.startOf('month').format(Config.DATE_FORMAT_SERVER)
          : null,
        note: values.note ? values.note : '',
      },
    };
    incentiveImport({
      variables,
    })
      .then(({ data }) => {
        const importIncentive = data.importIncentive;
        console.log('onIncentiveImportSubmit finished', importIncentive);
        if (importIncentive) {
          callBack({ status: true });
          onSuccessMutation();
          setShowAddIncentiveImportModal(false);
          setFileToImport(null);
        }
        setActiveIncentiveImport(null);
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = '';
        if (e.graphQLErrors) {
          e.graphQLErrors.forEach(({ extensions, message: errorMessage }) => {
            const { category, reason } = extensions;
            if (category === 'custom' && reason === 'Validation') {
              setImportErrors(errorMessage);
              setShowAddIncentiveImportErrorModal(true);
            } else {
              error = errorMessage;
              message.error(
                error ? error : 'Some error occured, Please try again !!!'
              );
            }
          });
        }
      });
  };

  const onMediaDownload = (values) => {
    console.log('onMediaDownload triggered with : ', values);
    const {
      media: [mediaItem],
    } = values;
    if (mediaItem && mediaItem.file_name) {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/media/download`, {
        method: 'POST',
        cache: 'no-cache',
        headers: new Headers({
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        }),
        body: JSON.stringify({
          mediaId: mediaItem.id,
        }),
      })
        .then((response) => {
          if (response.status === 200) {
            return response.blob();
          } else {
            throw new Error('Error');
          }
        })
        .then((blob) => {
          fileDownload(blob, mediaItem.file_name);
        })
        .catch((e) => {
          message.error('Something went wrong, Please try again!');
        });
    } else {
      message.error('Requested file not found!');
    }
  };

  const onDeleteConfirm = (incentiveImport) => {
    console.log('onDeleteConfirm called with///', incentiveImport);
    deleteIncentiveImport({
      variables: {
        id: incentiveImport.id,
      },
    })
      .then(({ data: { deleteIncentiveImport } }) => {
        console.log('onDeleteConfirm finished', deleteIncentiveImport);
        if (deleteIncentiveImport.id) {
          onSuccessMutation('Incentive Import deleted!');
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onImportModalClose = () => {
    setShowAddIncentiveImportModal(false);
    setActiveIncentiveImport(null);
    setFileToImport(null);
  };

  console.log('fetching ///2', fetching);

  return (
    <Spin
      spinning={
        loading || loadingPortfolios
        // || adding || updating || deleting
      }
    >
      <Card className="employeesCard" style={{ minHeight: '200px' }}>
        {/* {error && <Error message={error.message} />} */}

        {data && (
          <DataGrid
            dataSource={data.incentiveImports.data}
            columns={columns}
            rowSelection={rowSelection}
            filters={grid.metaData.filters}
            visibleColumns={grid.metaData.visibleColumns}
            sorting={grid.metaData.sorting}
            pagination={grid.metaData.pagination}
            onChange={onChange}
            totalRecords={data.incentiveImports.paginatorInfo.total}
            onClearFilter={() => {
              dispatch(resetGridFilters({ gridId }));
            }}
            onAddNewEntry={
              ability.toImportIncentive &&
              (() => {
                setShowAddIncentiveImportModal(true);
              })
            }
            addNewEntryBtnLabel="Import Incentives"
          />
        )}
      </Card>
      <Modal
        title={'Import Incentives'}
        visible={showAddIncentiveImportModal}
        confirmLoading={false}
        destroyOnClose={true}
        centered
        style={{ maxWidth: '700px' }}
        width="80%"
        footer={null}
        onCancel={onImportModalClose}
        maskClosable={false}
      >
        <Spin spinning={adding}>
          <AddIncentiveImportForm
            portfolios={portfolios ? portfolios.portfolios : []}
            onIncentiveImportSubmit={onIncentiveImportSubmit}
            onCancelClick={onImportModalClose}
            UploadFile={
              <UploadFile
                onUploadSuccess={(upload) => {
                  console.log('upload file done with///', upload);
                  setFileToImport(upload);
                }}
                onFileRemoved={(files) => {
                  console.log('upload file removed///', files);
                  setFileToImport(null);
                }}
                mediaType="application/vnd.ms-excel, .xlsx, .csv"
                buttonLabel="Select file to import"
              />
            }
            ability={ability}
          />
        </Spin>
      </Modal>
      <Modal
        visible={showAddIncentiveImportErrorModal}
        confirmLoading={false}
        destroyOnClose={true}
        centered
        style={{ maxWidth: '700px' }}
        width="80%"
        footer={null}
        onCancel={() => setShowAddIncentiveImportErrorModal(false)}
        maskClosable={false}
      >
        <br />
        <Alert message="Import Error" type="error" />
        <blockquote style={{ maxHeight: '300px', overflow: 'auto' }}>
          <pre>{importErrors}</pre>
        </blockquote>
      </Modal>
    </Spin>
  );
};

export default IncentiveImports;
